<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Page Header -->
      <breadcrumb :title="title" :path="path" :text="text" />

      <div class="row mt-3"><hr /></div>
      <div class="filter-card mb-3">
        <Form
          ref="filterForm"
          @submit="getAttendanceDetail"
          v-slot="{ errors }"
          :validation-schema="schema"
        >
        <div class="row align-items-end">
          <!-- division -->
          <div class="col-md-4">
            <div class="input-block mb-3">
              <label class="col-form-label"
                >Division<span class="text-danger">*</span></label
              >
              <Field
                name="division_id"
                :class="{ 'is-invalid': errors.division_id }"
                v-slot="{ field }"
              >
                <multiselect
                  v-bind="field"
                  v-model="selectedDivision"
                  :options="divisions"
                  label="label"
                  track-by="value"
                  placeholder="Select Division"
                  :loading="loadingDivision"
                  :class="{ 'is-invalid': errors.division_id }"
                  @select="handleDivisionChange"
                  @remove="handleDivisionRemove"
                />
                <div class="invalid-feedback">{{ errors.division_id }}</div>
              </Field>
            </div>
          </div>

          <!-- Department -->
          <div class="col-md-4">
            <div class="input-block mb-3">
              <label class="col-form-label">Department</label>
              <Field name="department_id" v-slot="{ field, errorMessage }">
                <multiselect
                  v-model="departmentSelection"
                  :options="departments"
                  label="label"
                  track-by="value"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :searchable="true"
                  placeholder="Select Department"
                  :class="{ 'is-invalid': errorMessage }"
                  @input="field.value = departmentSelection.map(d => d.id)"
                  :loading="loadingDepartment"
                  @select="handleDepartmentChange"
                  @remove="handleDepartmentChange"
                ></multiselect>
                <div class="invalid-feedback">{{ errorMessage }}</div>
              </Field>
            </div>
          </div>

          <!-- Designation -->
          <div class="col-md-4">
            <div class="input-block mb-3">
              <label class="col-form-label">Designation</label>
              <Field name="designation_id" v-slot="{ field, errorMessage }">
                <multiselect
                  v-model="designationSelection"
                  :options="designations"
                  label="label"
                  track-by="value"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :searchable="true"
                  placeholder="Select Designation"
                  :class="{ 'is-invalid': errorMessage }"
                  @input="field.value = designationSelection.map(d => d.id)"
                  :loading="loadingDesignation"
                ></multiselect>
                <div class="invalid-feedback">{{ errorMessage }}</div>
              </Field>
            </div>
          </div>

          <!-- Employee -->
          <div class="col-md-4">
            <div class="input-block mb-3">
              <label class="col-form-label">Employee</label>
              <Field name="employee_personal_id" v-slot="{ field }">
                <multiselect
                  v-bind="field"
                  v-model="selectedEmployees"
                  :options="employees"
                  label="label"
                  track-by="value"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :searchable="true"
                  :loading="loadingEmployees"
                  placeholder="Select Employee"
                />
              </Field>
            </div>
          </div>

          <!-- From -->
          <div class="col-md-4">
            <div class="input-block mb-3">
              <label class="col-form-label"
                >Start Month<span class="text-danger">*</span></label
              >
              <Field
                v-model="initialValues.month"
                name="month"
                type="month"
                class="form-control"
                :class="{ 'is-invalid': errors.month }"
              />
              <div class="invalid-feedback">{{ errors.month }}</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="input-block mb-3">
              <label class="col-form-label"
                >End Month<span class="text-danger">*</span></label
              >
              <Field
                v-model="initialValues.end_month"
                name="end_month"
                type="month"
                class="form-control"
                :class="{ 'is-invalid': errors.end_month }"
              />
              <div class="invalid-feedback">{{ errors.end_month }}</div>
            </div>
          </div>

          <!-- Search -->
          <div class="col-md-12 search-btn-col">
            <button type="button" class="btn btn-light" @click="resetFilters">Reset</button>
            <button :disabled="loadingTable" class="btn btn-info">
              Search
            </button>
          </div>
        </div>
        </Form>
      </div>

      <div class="row mt-3"><hr /></div>

      <div class="col-12" v-if="data.length > 0">
        <div class="submit-section d-flex justify-content-end mb-3">
          <button
            :disabled="loadingTable"
            class="btn add-btn export mb-2"
            @click="exportDetailReport"
          >
            <i class="fa-solid fa-file-excel"></i>
          </button>
        </div>
      </div>

      <div class="employestyle row">
        <b-tabs v-model="activeTab">
          
          <b-tab title="Report View" :active="activeTab === 0">
            <template v-if="activeTab === 0">
              <FlagAttributes v-if="data.length > 0  && totalRecords" :flagDefinitions="AttendaceFlags" :openByDefault="false"/>
              <a-table
                :columns="columns"
                :data-source="data"
                :pagination="false"
                row-key="employee_id"
                :loading="loadingTable"
                bordered
              />
            </template>
          </b-tab>
          <b-tab title="Exports" :active="activeTab === 1">
            <template v-if="activeTab === 1">
              <div class="d-flex justify-content-end">
                <button
                  class="btn btn-info mb-2"
                  :disabled="loadingExport"
                  @click="fetchExportData"
                >
                  Refresh
                </button>
              </div>
              <a-table
                :columns="exportColumns"
                :data-source="exportData"
                bordered
                :pagination="false"
                :loading="loadingExport"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.dataIndex === 'action'">
                    <div
                      v-if="record.status?.trim().toLowerCase() === 'completed'"
                      class="text-end"
                    >
                      <div class="dropdown-action">
                        <a-dropdown :trigger="['click']" placement="bottomRight">
                          <a href="javascript:;" class="action-dots"><i class="fa-solid fa-ellipsis-vertical"></i></a>
                          <template #overlay>
                            <a-menu>
                          <a-menu-item key="item-1"
                            @click="downloadFile(record)"
                          >
                            Download File
                          </a-menu-item>
                        </a-menu>
                          </template>
                        </a-dropdown>
                      </div>
                    </div>
                  </template>
                </template>
              </a-table>
            </template>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import { Form, Field } from "vee-validate";
import Multiselect from "vue-multiselect";
import moment from "moment";
import DivisionService from "@/services/DivisionService";
import DepartmentService from "@/services/DepartmentService";
import EmployeeService from "@/services/EmployeeService";
import DesignationService from "@/services/DesignationService";
import "vue-toast-notification/dist/theme-default.css";
import { notification } from "ant-design-vue";
import ComprehensiveReportService from "@/services/AttendanceComprehensiveReport";
import ExportBtns from "@/components/ExportBtns.vue";
import * as Yup from "yup";
import FlagAttributes from "@/components/FlagAttributeGuide.vue";

const exportColumns = [
  { title: "id", dataIndex: "job_id" },
  { title: "File Name", dataIndex: "filename" },
  { title: "Status", dataIndex: "status" },
  { title: "Error", dataIndex: "errors" },
  { title: "Date", dataIndex: "generated_date" },

  {
    title: "Action",
    dataIndex: "action",
    key: "action",
  },
];
const columns = [
  {
    title: "Department",
    dataIndex: "department_name",
  },
  {
    title: "Hcm ID",
    dataIndex: "employee_id",
  },
  {
    title: "Employee Name",
    dataIndex: "full_name",
  },
  {
    title: "Flag / Attribute",
    dataIndex: "flag",
  },
  {
    title: "Count",
    dataIndex: "count",
  },
];
const AttendaceFlags=[
          {
          code: "A",
          meaning: "ABSENT"
        },
        {
          code: "A-RA",
          meaning: "ABSENT - ROSTER ABSENT"
        },
        {
          code: "A-MAP",
          meaning: "ABSENT - MINIMUM ATTENDANCE POLICY"
        },
        {
          code: "A-NJ",
          meaning: "ABSENT - NOT JOINED"
        },
        {
          code: "A-NCNS",
          meaning: "ABSENT - NO CALL NO SHOW"
        },
        {
          code: "A-SUSPEND",
          meaning: "ABSENT - SUSPENDED"
        },
        {
          code: "EO",
          meaning: "EARLY OUT"
        },
        {
          code: "EO + PL",
          meaning: "EARLY OUT + PAID LEAVE"
        },
        {
          code: "LA",
          meaning: "LATE ARRIVAL"
        },
        {
          code: "LA + PL",
          meaning: "LATE ARRIVAL + PAID LEAVE"
        },
        {
          code: "OFF - RD",
          meaning: "OFF - REST DAY"
        },
        {
          code: "OFF - GH",
          meaning: "OFF - GAZETTED HOLIDAY"
        },
        {
          code: "OFF - WH",
          meaning: "OFF - WEEKLY HOLIDAY"
        },
        {
          code: "P - FD",
          meaning: "PRESENT - FULL DAY"
        },
        {
          code: "UHD",
          meaning: "UNPAID HALF DAY"
        },
        {
          code: "HD",
          meaning: "HALF DAY"
        },
        {
          code: "Res. / Term. ",
          meaning: "RESIGNED/TERMINATED"
        },
        {
          code: "P - (PL + ATT.)",
          meaning: "PRESENT - (PAID LEAVE + ATTENDANCE)"
        },
        
]
export default {
  components: {
    Form,
    Field,
    Multiselect,
    ExportBtns,
    FlagAttributes,
  },

  data() {
    return {
      title: "Attendance Comprehensive Report",
      path: "Reports and Analytics",
      text: "Attendance Comprehensive Report",
      divisions: [],
      departments: [],
      employees: [],
      designations: [],
      selectedDivision: null,
      departmentSelection: [],
      selectedEmployees: [],
      designationSelection: [],
      loadingDivision: false,
      loadingDepartment: false,
      loadingEmployees: false,
      loadingDesignation: false,
      loadingTable: false,
      initialValues: {
        month: "",
        end_month: "",
      },
      data: [],
      tableData: [],
      columns,
      payload: {},
      totalCounts: null,
      activeTab: 0,
      exportColumns,
      exportData: [],
      loadingExport: false,
      AttendaceFlags,
    };
  },

  methods: {
    async exportDetailReport() {
      this.loadingTable = true;

      try {
        const res = await ComprehensiveReportService.generateAndDownload(
          this.payload,
        );
        this.activeTab = 1;
        this.fetchExportData();
        // console.log("generateAndDownload response:", res);

        // const fileId =
        //   res?.id || res?.data?.id || res?.job_id || res?.data?.job_id;

        // if (!fileId) {
        //   throw new Error("No file id returned from API");
        // }

        // // download file
        // const fileData = await ComprehensiveReportService.downloadExcel(fileId);

        // const blob = new Blob([fileData], {
        //   type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        // });

        // const url = window.URL.createObjectURL(blob);
        // const a = document.createElement("a");
        // a.href = url;
        // a.download = "Comprehensive_Report.xlsx";
        // document.body.appendChild(a);
        // a.click();
        // document.body.removeChild(a);
        // window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Export error:", error);
      } finally {
        this.loadingTable = false;
      }
    },
    async exportPDF() {
      this.loadingTable = true;
      try {
        const response =
          await AttendanceConsolidatedReportService.exportPdfDetailReport(
            this.payload,
            // { responseType: "arraybuffer" },
          );
        if (response.success == true) {
          notification.open({
            message: response.message,
            placement: "topRight",
            bottom: "50px",
            duration: 3,
            style: {
              width: "350px",
              marginLeft: `${335 - 600}px`,
              background: "#2ab57d",
            },
          });
        }
        await this.fetchExportData();
        this.activeTab = 1;
      } catch (error) {
        if (error.response && error.response.status === 400) {
          notification.open({
            message: error.response.data.message,
            placement: "topRight",
            bottom: "10px",
            duration: 3,
            style: {
              width: "350px",
              marginLeft: `${335 - 600}px`,
              background: "#fd625e",
            },
          });
        }
      } finally {
        this.loadingTable = false;
      }
    },
    async getAttendanceDetail(values) {
      this.loadingTable = true;
      this.activeTab = 0;
      // return;
      try {
        this.payload = {
          month: moment(values?.month?.split("-")[1])
            .format("MMMM")
            .toLowerCase(),
          year: values?.month?.split("-")[0],
          end_month: moment(values?.end_month?.split("-")[1])
            .format("MMMM")
            .toLowerCase(),
          end_year: values?.end_month?.split("-")[0],
          division_id: Number(this.selectedDivision?.value),
          department_id: this.departmentSelection.length
            ? this.departmentSelection.map((d) => Number(d.value))
            : null,
          designation_id: this.designationSelection.length
            ? this.designationSelection.map((d) => Number(d.value))
            : null,
          employee_personal_id: (this.selectedEmployees || []).map(
            (el) => el.value,
          ),
        };

        const response = await ComprehensiveReportService.generateReport(
          this.payload,
        );
        this.data = response.data || [];
        if (response.success == true) {
          notification.open({
            message: response.message,
            placement: "topRight",
            bottom: "50px",
            duration: 3,
            style: {
              width: "350px",
              marginLeft: `${335 - 600}px`,
              background: "#2ab57d",
            },
          });
        }
        this.totalRecords = this.data.length;
      } catch (error) {
        if (error.response && error.response.status === 400) {
          notification.open({
            message: error.response.message[0],
            placement: "topRight",
            bottom: "10px",
            duration: 3,
            style: {
              width: "350px",
              marginLeft: `${335 - 600}px`,
              background: "#fd625e",
            },
          });
        }
      } finally {
        this.loadingTable = false;
      }
    },

    async fetchExportData() {
      this.loadingExport = true;
      try {
        const response =
          await ComprehensiveReportService.fetchGeneratedReports();
        this.exportData = response.data || [];
      } catch (error) {
        if (error.response && error.response.status === 400) {
          notification.open({
            message: error.response.message[0],
            placement: "topRight",
            bottom: "10px",
            duration: 3,
            style: {
              width: "350px",
              marginLeft: `${335 - 600}px`,
              background: "#fd625e",
            },
          });
        }
      } finally {
        this.loadingExport = false;
      }
    },
    async handleDivisionChange(selectedDivision) {
      if (!selectedDivision) {
        this.departments = [];
        this.departmentSelection = [];
        this.employees = [];
        this.selectedEmployees = [];
        return;
      }
      this.loadingDepartment = true;
      this.loadingEmployees = true;
      this.departmentSelection = [];
      this.selectedEmployees = [];
      try {
        const responseDept = await DepartmentService.getDepartmentsDivisionWise(
          selectedDivision.value,
        );
        this.departments = responseDept.data.map((el) => ({
          value: el.id,
          label: el.name,
        }));
        const responseEmp = await EmployeeService.fetchDivisionWiseEmployee({
          division_id: selectedDivision.value,
        });
        this.employees = responseEmp.data.map((el) => ({
          value: el.id,
          label: el.name,
        }));
      } finally {
        this.loadingDepartment = false;
        this.loadingEmployees = false;
      }
    },
    async handleDivisionRemove() {
      this.departments = [];
      this.departmentSelection = [];
      this.employees = [];
      this.selectedEmployees = [];
    },
    resetFilters() {
      this.selectedDivision = null;
      this.departments = [];
      this.departmentSelection = [];
      this.designationSelection = [];
      this.employees = [];
      this.selectedEmployees = [];
      this.initialValues = { month: "", end_month: "" };
      this.data = [];
      this.$refs.filterForm?.resetForm();
    },
    async handleDepartmentChange(element) {
      if (!element) {
        this.employees = [];
        this.selectedEmployees = [];
        return;
      }
      this.loadingEmployees = true;
      this.selectedEmployees = [];
      this.employees = [];
      try {
        if (this.departmentSelection.length > 0) {
          this.departmentSelection.forEach(async (el) => {
            const responseEmp = await EmployeeService.getDivisionDepartmentEmployees(
              this.selectedDivision.value,
              el.value,
            );
            const temp = responseEmp.data.map((el) => {
              return {
                value: el.id,
                label: el.employee_name,
              };
            });
            this.employees.push(...temp);
          });
        } else {
          this.handleDivisionChange(this.selectedDivision);
        }
      } finally {
        this.loadingEmployees = false;
      }
    },
    async fetchDesignations() {
      this.loadingDesignation = true;
      try {
        const res = await DesignationService.getDesignations();
        this.designations = res.data.map((el) => ({
          value: el.id,
          label: el.designation_name,
        }));
      } finally {
        this.loadingDesignation = false;
      }
    },
    async downloadFile(record) {
      const id = record?.job_id;

      try {
        const fileData = await ComprehensiveReportService.downloadExcel(id);

        const blob = new Blob([fileData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = record.filename || "Report.xlsx";

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download error:", error);
      }
    },
  },
  computed: {
    currentRecord() {
      return this.data[this.currentIndex] || null;
    },
  },
  async created() {
    // Load initial divisions and employees
    this.loadingDivision = true;
    this.loadingEmployees = true;
    try {
      const resDiv = await DivisionService.getDivisions();
      this.divisions = resDiv.data.map((d) => ({ value: d.id, label: d.name }));

      await this.fetchExportData();
      await this.fetchDesignations();
    } finally {
      this.loadingDivision = false;
      this.loadingEmployees = false;
    }
  },
  setup() {
    const schema = Yup.object().shape({
      division_id: Yup.mixed().required("Division is required"),
      month: Yup.string().required("Start month is required"),
      end_month: Yup.string().required("End month is required"),
    });
    return {
      schema,
    };
  },
};
</script>

<style scoped>
.attendance-detail-table th {
  text-align: center;
  font-weight: 600;
  font-size: 13px;
}

.attendance-detail-table td {
  text-align: center;
  font-size: 13px;
  white-space: nowrap;
}

.employee-summary-box-wrapper {
  background: #f5f5f5;
  padding: 12px 20px;
  font-size: 13px;
  border: 1px solid #ddd;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.employee-summary-box div {
  min-width: 150px;
  margin-right: 15px;
}
.employee-summary-box {
  display: flex;
  flex-wrap: wrap; /* allows items to wrap to next line */
  gap: 20px; /* space between fields */
}

.employee-field {
  min-width: 200px; /* adjust based on how many fields per row you want */
  flex: 1; /* allows fields to grow */
}

.employee-summary-box strong {
  font-weight: 600;
}

.employee-summary-box .text-primary {
  color: #1890ff;
  font-weight: 600;
}
.employee-summary-box-wrapper {
  background: #f5f5f5;
  padding: 12px 20px;
  font-size: 13px;
  border: 1px solid #ddd;
  margin-bottom: 10px;
}

.employee-summary-box {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(3, 1fr); /* 2 columns */
  row-gap: 20px; /* vertical spacing */
}

.carousel-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
}

.carousel-counter {
  font-size: 16px;
  font-weight: 500;
  color: #666;
}

.carousel-content {
  min-height: 400px;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
}

.carousel-footer {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.jump-to-record {
  display: flex;
  align-items: center;
}

/* Slide animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Keyboard navigation hint */
.carousel-container:focus {
  outline: 2px solid #1890ff;
  outline-offset: 2px;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
